import { Col, Row, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { REQUEST_LOADING_STATES } from "../../../helpers/constant";
import { AppDispatch, StoreState } from "../../../redux/store";
import { addRecentSeacrchUser, search, searchMoreUsers } from "../../../redux/users/usersSlice";
import SpinnerComponent from "../../SpinnerComponent/SpinnerComponent";
import "./index.less";
import SearchSkeletonComponent from "./SearchSkeletonComponent";

const NB_USER_TO_SHOW = 15;

type IsSearchingProps = {
  isSearching: boolean;
  searchValue: string;
};

const IsSearchingComponent: React.FC<IsSearchingProps> = ({ isSearching, searchValue }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { list: creators, total, loading } = useSelector((state: StoreState) => state.users);
  const [seeAllResults, setSeeAllResults] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (searchValue !== "") {
      const payload = {
        q: searchValue,
        limit: NB_USER_TO_SHOW,
      };
      setIsLoading(true);
      const delayDebounceFn = setTimeout(() => {
        dispatch(search(payload));
      }, 300);

      return () => {
        clearTimeout(delayDebounceFn);
      };
    }
  }, [searchValue]);

  useEffect(() => {
    if (loading === REQUEST_LOADING_STATES.SUCCEEDED || loading === REQUEST_LOADING_STATES.FAILED) {
      setIsLoading(false);
    }
  }, [loading]);

  const onProfileClick = (id: string, username: string) => {
    if (id !== "") {
      dispatch(addRecentSeacrchUser(id));
      navigate(`/p/${username}`);
    }
  };

  function loadMoreUsers() {
    if (isSearching) {
      const nextPage = page + 1;
      const totalPages = Math.ceil(total! / NB_USER_TO_SHOW);
      const payload = {
        q: searchValue,
        limit: NB_USER_TO_SHOW,
        offset: page * NB_USER_TO_SHOW,
      };
      dispatch(searchMoreUsers(payload));
      if (nextPage >= totalPages) {
        setHasNextPage(false);
      }
      setPage(nextPage);
    }
  }

  const [sentryRef] = useInfiniteScroll({
    loading: loading === REQUEST_LOADING_STATES.PENDING,
    hasNextPage,
    onLoadMore: loadMoreUsers,
  });

  return (
    <div className="w-full" style={{ height: "100%" , overflowY: "scroll"}}>
      {creators.length === 0 &&
        (loading === REQUEST_LOADING_STATES.PENDING || isLoading ? (
          <SearchSkeletonComponent />
        ) : (
          <Row>
            <Typography.Text className="no-creator-found">{t("no-creator-found")}</Typography.Text>
          </Row>
        ))}
      {creators &&
        creators.map((creator, index) => {
          return (index < NB_USER_TO_SHOW && !seeAllResults) || seeAllResults ? (
            <Col span={24}>
              <Row
                key={creator._id}
                onClick={() => onProfileClick(creator._id !== undefined ? creator._id : "", creator.username!)}
                className="justify-between items-center mt-10"
              >
                <Col span={24}>
                  <Row className="items-center">
                    <img
                      className="avatar"
                      src={creator.avatar !== undefined ? creator.avatar : ""}
                      alt={creator.avatar !== undefined ? creator.avatar : ""}
                    />
                    <Space direction="vertical" className="recentVisitedTextWrapper">
                      <Typography.Text className="username">{t(`${creator.username !== undefined ? creator.username : ""}`)}</Typography.Text>
                      <Typography.Text className="firstName">{creator.name}</Typography.Text>
                    </Space>
                  </Row>
                </Col>
              </Row>
            </Col>
          ) : null;
        })}

      {creators && !seeAllResults && total! > NB_USER_TO_SHOW ? (
        <Row className={`mt-16 mx-auto ${seeAllResults ? "d-none" : ""}`}>
          <Typography.Text
            onClick={() => {
              setSeeAllResults(true);
              setHasNextPage(true);
            }}
            className="SearchSeeAllResults mx-auto"
          >
            {t("see-all-results")}
          </Typography.Text>
        </Row>
      ) : null}

      {(loading || hasNextPage) && seeAllResults && <SpinnerComponent refProp={sentryRef} />}
    </div>
  );
};

export default IsSearchingComponent;
